import { Scene } from 'three'
import Debug from './Utils/Debug.js'
import Sizes from './Utils/Sizes.js'
import Time from './Utils/Time.js'
import { isMobile } from './Utils/Device.js'

import Camera from './Core/Camera.js'
import Timeline from './Core/Timeline.js'
import Renderer from './Core/Renderer.js'
import Composer from './Core/Composer.js'
import World from './World/World.js'
import UI from './UI/UI.js'
import Labels from './UI/Labels.js'
import BallTrajectory from './UI/BallTrajectory.js'
import TeeSelection from './UI/TeeSelection.js'
import Info from './UI/Info.js'
import Presentation from './UI/Presentation.js'
import ModeToggle from './UI/ModeToggle.js'
import MapView from './UI/MapView.js'
import Finale from './UI/Finale.js'
import Resources from './Utils/Resources.js'
import CourseRoute from './Core/CourseRoute.js'

import sources from './sources.js'
import config from './config.js'

let instance = null

export default class Experience {
    constructor(_canvas) {
        if (instance) return instance
        instance = this

        window.experience = this

        this.canvas = _canvas

        this.debug = new Debug()
        this.resources = new Resources(sources)
        this.config = config

        this.scene = new Scene()

        this.initUtilities()

        this.resources.on('ready', () => {
            this.initCore()
            this.initWorld()
            this.initUI()
            this.initEvents()
        })
    }

    /* ---------------- UTILS ---------------- */

    initUtilities() {
        this.isMobile = isMobile()
        this.sizes = new Sizes()
        this.time = new Time()
    }

    /* ---------------- CORE ---------------- */

    initCore() {
        this.camera = new Camera()
        this.renderer = new Renderer()
    }


    initWorld() {
        this.world = new World()

        // Fenêtres de progression des trous, calculées depuis le parcours
        this.course = new CourseRoute()
        this.config.holes = this.course.holes
    }

    initUI() {
        this.ui = new UI()
        this.labels = new Labels()
        this.ballTrajectory = new BallTrajectory()
        this.info = new Info()
        this.presentation = new Presentation()
    }

    /* ---------------- LOOP ---------------- */

    initEvents() {
        this.composer = new Composer()

        this.sizes.on('resize', () => this.resize())

        this.time.on('tick', () => this.update())
        this.timeline = new Timeline()
        this.teeSelection = new TeeSelection()
        this.mapView = new MapView()
        this.modeToggle = new ModeToggle()
        this.finale = new Finale()
    }

    /* ---------------- RESIZE ---------------- */

    resize() {
        this.camera.resize()
        this.renderer.resize()
        this.composer.resize()
    }

    /* ---------------- UPDATE LOOP ---------------- */

    update() {
        this.camera.update()
        this.timeline.update()
        this.composer.update()
        this.world.update()
        this.labels.update()
        this.info.update()
        this.presentation?.update()
        this.finale?.update(this.timeline?.progress ?? 0)
    }

    /* ---------------- DESTROY ---------------- */

    destroy() {
        this.sizes.off('resize')
        this.time.off('tick')

        this.timeline?.destroy?.()
        this.teeSelection?.destroy?.()
        this.modeToggle?.destroy?.()
        this.mapView?.destroy?.()
        this.finale?.destroy?.()
        this.ballTrajectory?.destroy?.()
        this.camera.destroy()
        this.composer.destroy()
        this.world.destroy()
        this.presentation?.destroy?.()
        this.ui.destroy()

        if (this.debug?.ui) {
            this.debug.ui.destroy?.()
        }

        this.scene.traverse(obj => {
            if (!obj.isMesh) return

            obj.geometry?.dispose?.()

            if (Array.isArray(obj.material)) {
                obj.material.forEach(m => m.dispose())
            } else {
                obj.material?.dispose?.()
            }
        })

        this.renderer.destroy()

        this.scene = null
        this.camera = null
        this.timeline = null
        this.world = null
        this.ui = null
        this.debug = null
        this.renderer = null
    }
}